import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Package, Plus, Trash, CurrencyInr, Sparkle, Warning, X, PencilSimple } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { formatINR } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import PaymentModal, { PaymentStatusPill } from "../../components/PaymentModal";
import PurchaseAutoFetchModal from "../../components/PurchaseAutoFetchModal";

const today = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

const mkItem = () => ({
  _uid: `pi_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  description: "", hsn: "", quantity: 1, unit: "Unit", unit_price: 0, amount: 0, gst_percent: 18, kind: "inventory",
});

const emptyForm = {
  vendor_id: "", vendor_name: "",
  bill_no: "", bill_date: today(), due_date: addDays(today(), 30),
  place_of_supply: "Delhi", is_igst: false,
  items: [mkItem()],
  notes: "",
};

export default function Purchases() {
  const { active, activeId } = useCompany();
  const [list, setList] = useState([]);
  const [month, setMonth] = useState(thisMonth());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fetchOpen, setFetchOpen] = useState(false);
  const [autoFetchedBanner, setAutoFetchedBanner] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [overdue, setOverdue] = useState({ rows: [], vendor_rollup: [], total_outstanding: 0, bill_count: 0 });

  const load = async () => {
    if (!activeId) { setList([]); return; }
    try {
      const { data } = await api.get("/finance/purchases", { params: { company_id: activeId, month } });
      setList(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    try {
      const { data: od } = await api.get("/finance/purchases/overdue", { params: { company_id: activeId, days: 30 } });
      setOverdue(od || { rows: [], vendor_rollup: [], total_outstanding: 0, bill_count: 0 });
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId, month]);

  // Recompute amount on a line whenever qty / rate changes
  const updateItem = (idx, patch) => {
    setForm((f) => {
      const items = f.items.map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, ...patch };
        const q = Number(next.quantity) || 0;
        const r = Number(next.unit_price) || 0;
        if ("quantity" in patch || "unit_price" in patch) next.amount = +(q * r).toFixed(2);
        return next;
      });
      return { ...f, items };
    });
  };

  const totals = useMemo(() => {
    let sub = 0, gst = 0;
    (form.items || []).forEach((it) => {
      const amt = Number(it.amount) || (Number(it.quantity || 0) * Number(it.unit_price || 0));
      sub += amt;
      gst += amt * (Number(it.gst_percent) || 0) / 100;
    });
    const subR = +sub.toFixed(2);
    const gstR = +gst.toFixed(2);
    const cgst = form.is_igst ? 0 : +(gstR / 2).toFixed(2);
    const sgst = form.is_igst ? 0 : +(gstR / 2).toFixed(2);
    const igst = form.is_igst ? gstR : 0;
    const grand = +(subR + gstR).toFixed(2);
    return { subtotal: subR, cgst, sgst, igst, gst: gstR, grand_total: grand };
  }, [form.items, form.is_igst]);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm, bill_date: today(), due_date: addDays(today(), 30), items: [mkItem()] });
    setAutoFetchedBanner(null);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setAutoFetchedBanner(null);
    setForm({
      vendor_id: p.vendor_id || "", vendor_name: p.vendor_name || "",
      bill_no: p.bill_no || "", bill_date: p.bill_date || today(),
      due_date: p.due_date || addDays(p.bill_date || today(), 30),
      place_of_supply: p.place_of_supply || "Delhi", is_igst: !!p.is_igst,
      items: (p.items || []).map((it) => ({ ...mkItem(), ...it })),
      notes: p.notes || "",
    });
    setOpen(true);
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete purchase from ${p.vendor_name} (₹${Number(p.grand_total).toLocaleString("en-IN")})?`)) return;
    try {
      await api.delete(`/finance/purchases/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const save = async () => {
    if (!activeId) { toast.error("Pick a company first"); return; }
    if (!form.vendor_name.trim()) { toast.error("Vendor name required"); return; }
    if (!form.items.length || !form.items.some((it) => it.description.trim())) { toast.error("Add at least one item with description"); return; }
    setSaving(true);
    try {
      const payload = {
        company_id: activeId,
        vendor_id: form.vendor_id || "",
        vendor_name: form.vendor_name.trim(),
        bill_no: (form.bill_no || "").trim(),
        bill_date: form.bill_date,
        due_date: form.due_date,
        place_of_supply: form.place_of_supply,
        is_igst: !!form.is_igst,
        items: form.items.filter((it) => it.description.trim()).map((it) => {
          const { _uid, ...clean } = it;
          return {
            description: clean.description, hsn: clean.hsn || "",
            quantity: Number(clean.quantity) || 0, unit: clean.unit || "Unit",
            unit_price: Number(clean.unit_price) || 0,
            amount: Number(clean.amount) || (Number(clean.quantity) * Number(clean.unit_price)) || 0,
            gst_percent: Number(clean.gst_percent) || 0,
            kind: clean.kind === "service" ? "service" : "inventory",
          };
        }),
        subtotal: totals.subtotal, cgst: totals.cgst, sgst: totals.sgst, igst: totals.igst,
        grand_total: totals.grand_total, notes: form.notes || "",
      };
      if (editingId) {
        await api.put(`/finance/purchases/${editingId}`, payload);
        toast.success("Purchase updated");
      } else {
        await api.post("/finance/purchases", payload);
        toast.success("Purchase recorded");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleAutoFetched = (data) => {
    const p = data.purchase_prefill || {};
    setEditingId(null);
    setForm({
      vendor_id: p.vendor_id || "",
      vendor_name: p.vendor_name || "",
      bill_no: p.bill_no || "",
      bill_date: p.bill_date || today(),
      due_date: p.due_date || addDays(p.bill_date || today(), 30),
      place_of_supply: p.place_of_supply || "Delhi",
      is_igst: !!p.is_igst,
      items: (p.items || []).map((it) => ({ ...mkItem(), ...it, kind: it.kind || "inventory" })),
      notes: p.notes || "",
    });
    setAutoFetchedBanner({
      vendor: data.vendor?.name, bill_no: data.bill?.bill_no,
      grand_total: data.bill?.grand_total, items: (data.bill?.items || []).length,
      created_new: data.vendor?.created_new,
    });
    setOpen(true);
  };

  // Group purchases by bill_date month (for monthly view headers)
  const stats = useMemo(() => {
    let total = 0, paid = 0, out = 0, overdueCount = 0;
    (list || []).forEach((p) => {
      total += Number(p.grand_total) || 0;
      paid += Number(p.paid) || 0;
      out += Number(p.outstanding) || 0;
      if (p.overdue) overdueCount += 1;
    });
    return { total, paid, out, overdueCount };
  }, [list]);

  return (
    <Layout
      title="Purchases · COGS"
      subtitle={`${active?.name || "—"} · vendor bills for goods/services that go into your business`}
      actions={
        <>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 rounded-none" data-testid="pur-month-picker"/>
          <button onClick={() => setFetchOpen(true)} data-testid="pur-auto-fetch-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white">
            <Sparkle size={14} weight="bold"/> Auto-Fetch from Bill
          </button>
          <button onClick={openNew} data-testid="pur-new-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] active:scale-95">
            <Plus size={16}/> New Purchase
          </button>
        </>
      }
    >
      {/* Headline KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 border border-black/10 bg-white mb-6">
        <Kpi label="Total purchases" value={formatINR(stats.total)} testid="pur-kpi-total"/>
        <Kpi label="Paid" value={formatINR(stats.paid)} tone="ok"/>
        <Kpi label="Outstanding" value={formatINR(stats.out)} tone={stats.out > 0 ? "warn" : "neutral"}/>
        <Kpi label="Overdue (>30 days)" value={String(overdue.bill_count)} sub={overdue.bill_count ? formatINR(overdue.total_outstanding) : ""} tone={overdue.bill_count ? "danger" : "neutral"} testid="pur-kpi-overdue"/>
      </div>

      {/* Overdue vendors widget */}
      {overdue.vendor_rollup?.length > 0 && (
        <div className="border border-[#D90429]/30 bg-[#D90429]/5 p-4 mb-6" data-testid="pur-overdue-widget">
          <div className="flex items-center gap-2 mb-3">
            <Warning size={18} weight="bold" className="text-[#D90429]"/>
            <h3 className="font-display text-lg tracking-tight">Vendor outstanding · over 30 days</h3>
            <span className="ml-auto text-[11px] font-mono-shelv text-[#D90429] font-bold">{formatINR(overdue.total_outstanding)} across {overdue.bill_count} bill(s)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] text-[#717680] uppercase font-mono-shelv tracking-widest">
                <tr><th className="py-1.5 pr-3">Vendor</th><th className="py-1.5 pr-3">Bills</th><th className="py-1.5 pr-3">Max overdue</th><th className="py-1.5 pr-3 text-right">Outstanding</th></tr>
              </thead>
              <tbody>
                {overdue.vendor_rollup.slice(0, 8).map((v, i) => (
                  <tr key={i} className="border-t border-[#D90429]/10" data-testid={`pur-overdue-row-${i}`}>
                    <td className="py-2 pr-3 font-semibold">{v.vendor_name}</td>
                    <td className="py-2 pr-3 font-mono-shelv">{v.bills}</td>
                    <td className="py-2 pr-3"><span className="text-[11px] font-mono-shelv font-bold text-[#D90429]">{v.max_days_overdue} days</span></td>
                    <td className="py-2 pr-3 text-right font-mono-shelv font-bold">{formatINR(v.total_outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Purchases table */}
      {list.length === 0 ? (
        <div className="border border-black/10 bg-white p-16 text-center text-[#717680]" data-testid="pur-empty">
          <Package size={36} className="mx-auto text-[#D90429] mb-3"/>
          <p>No purchases recorded for <b>{month}</b>. Click <b>Auto-Fetch from Bill</b> to parse a vendor PDF, or <b>New Purchase</b> to record manually.</p>
        </div>
      ) : (
        <div className="border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]" data-testid="pur-table">
            <thead className="bg-[#F5F6F8] text-left">
              <tr>
                {["Date", "Vendor", "Bill #", "Items", "Grand total", "Outstanding", "Due", "Status", ""].map((h) => (
                  <th key={h} className="px-3 py-2.5 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`pur-row-${p.id}`}>
                  <td className="px-3 py-2.5 font-mono-shelv">{p.bill_date}</td>
                  <td className="px-3 py-2.5"><div className="font-semibold">{p.vendor_name}</div></td>
                  <td className="px-3 py-2.5 font-mono-shelv">{p.bill_no || "—"}</td>
                  <td className="px-3 py-2.5">
                    <span className="text-[11px] font-mono-shelv text-[#4A4D57]">{(p.items || []).length} line(s)</span>
                    <div className="flex gap-1 mt-0.5">
                      {(p.items || []).slice(0, 3).map((it, i) => (
                        <span key={i} className={`text-[9px] px-1 py-0.5 font-mono-shelv uppercase tracking-wider ${it.kind === "service" ? "bg-[#7C3AED]/10 text-[#7C3AED]" : "bg-[#0D9488]/10 text-[#0D9488]"}`}>
                          {it.kind === "service" ? "SVC" : "INV"}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 font-mono-shelv font-semibold">{formatINR(p.grand_total)}</td>
                  <td className="px-3 py-2.5 font-mono-shelv text-[#D90429]">{p.outstanding > 0 ? formatINR(p.outstanding) : "—"}</td>
                  <td className="px-3 py-2.5 font-mono-shelv text-[11px]">{p.due_date}</td>
                  <td className="px-3 py-2.5">
                    <div className="inline-flex items-center gap-1.5">
                      <PaymentStatusPill status={p.payment_status} />
                      {p.overdue && (
                        <span className="text-[9px] px-1.5 py-0.5 bg-[#D90429] text-white font-mono-shelv font-bold tracking-wider" data-testid={`pur-overdue-${p.id}`}>OVERDUE</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => setPayTarget(p)} className="p-1.5 hover:bg-[#0D9488]/10 text-[#0D9488]" title="Record payment" data-testid={`pay-pur-${p.id}`}><CurrencyInr size={13}/></button>
                    <button onClick={() => openEdit(p)} className="p-1.5 hover:bg-black/5" data-testid={`edit-pur-${p.id}`}><PencilSimple size={13}/></button>
                    <button onClick={() => remove(p)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`del-pur-${p.id}`}><Trash size={13}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editingId ? "Edit purchase" : "New purchase"}</DialogTitle>
          </DialogHeader>
          {autoFetchedBanner && (
            <div className="border border-[#0D9488]/30 bg-[#0D9488]/5 px-3 py-2 text-xs flex flex-wrap items-center gap-3" data-testid="pur-autofetch-banner">
              <Sparkle size={14} weight="bold" className="text-[#0D9488]"/>
              <span className="font-semibold text-[#0A0A0A]">Auto-fetched from vendor bill</span>
              <span className="font-mono-shelv text-[#4A4D57]">Vendor: <b>{autoFetchedBanner.vendor}</b>{autoFetchedBanner.created_new && <span className="ml-1 text-[10px] px-1 py-0.5 bg-[#0D9488] text-white">NEW</span>}</span>
              {autoFetchedBanner.bill_no && <span className="font-mono-shelv text-[#4A4D57]">Bill #: <b>{autoFetchedBanner.bill_no}</b></span>}
              {autoFetchedBanner.grand_total > 0 && <span className="font-mono-shelv text-[#4A4D57]">Total: <b>{formatINR(autoFetchedBanner.grand_total)}</b></span>}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2"><Label>Vendor *</Label><Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} className="rounded-none" data-testid="pur-vendor-input"/></div>
            <div><Label>Bill #</Label><Input value={form.bill_no} onChange={(e) => setForm({ ...form, bill_no: e.target.value })} className="rounded-none"/></div>
            <div><Label>Place of supply</Label><Input value={form.place_of_supply} onChange={(e) => setForm({ ...form, place_of_supply: e.target.value })} className="rounded-none"/></div>
            <div><Label>Bill date *</Label><Input type="date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value, due_date: addDays(e.target.value, 30) })} className="rounded-none"/></div>
            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-none"/></div>
            <div>
              <Label>GST type</Label>
              <Select value={form.is_igst ? "igst" : "cgst_sgst"} onValueChange={(v) => setForm({ ...form, is_igst: v === "igst" })}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cgst_sgst">CGST + SGST (intra-state)</SelectItem>
                  <SelectItem value="igst">IGST (inter-state)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Items */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label>Line items *</Label>
              <button onClick={() => setForm({ ...form, items: [...form.items, mkItem()] })} className="text-xs font-mono-shelv uppercase tracking-widest text-[#0D9488] hover:text-[#0F766E] inline-flex items-center gap-1" data-testid="pur-add-item">
                <Plus size={12} weight="bold"/> Add line
              </button>
            </div>
            <div className="border border-black/10 overflow-x-auto">
              <table className="w-full text-xs min-w-[800px]" data-testid="pur-items-table">
                <thead className="bg-[#F5F6F8] text-left">
                  <tr>
                    {["Type", "Description", "HSN", "Qty", "Unit", "Rate", "Amount", "GST%", ""].map((h, i) => (
                      <th key={i} className="px-2 py-1.5 font-mono-shelv text-[9px] uppercase tracking-widest text-[#717680]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, idx) => (
                    <tr key={it._uid || idx} className="border-t border-black/5">
                      <td className="px-1.5 py-1">
                        <Select value={it.kind} onValueChange={(v) => updateItem(idx, { kind: v })}>
                          <SelectTrigger className="rounded-none h-8 text-[11px] w-24"><SelectValue/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inventory">Inventory</SelectItem>
                            <SelectItem value="service">Service</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-1.5 py-1"><Input value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })} className="rounded-none h-8 text-[12px]" placeholder="Item / service"/></td>
                      <td className="px-1.5 py-1"><Input value={it.hsn} onChange={(e) => updateItem(idx, { hsn: e.target.value })} className="rounded-none h-8 text-[12px] w-20"/></td>
                      <td className="px-1.5 py-1"><Input type="number" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} className="rounded-none h-8 text-[12px] w-16"/></td>
                      <td className="px-1.5 py-1"><Input value={it.unit} onChange={(e) => updateItem(idx, { unit: e.target.value })} className="rounded-none h-8 text-[12px] w-16"/></td>
                      <td className="px-1.5 py-1"><Input type="number" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} className="rounded-none h-8 text-[12px] w-20"/></td>
                      <td className="px-1.5 py-1 font-mono-shelv font-semibold">₹{Number(it.amount || 0).toLocaleString("en-IN")}</td>
                      <td className="px-1.5 py-1"><Input type="number" value={it.gst_percent} onChange={(e) => updateItem(idx, { gst_percent: e.target.value })} className="rounded-none h-8 text-[12px] w-14"/></td>
                      <td className="px-1.5 py-1">
                        <button onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })} className="p-1 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`pur-remove-item-${idx}`}><X size={12} weight="bold"/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end mt-4">
            <div className="text-sm space-y-1 min-w-[260px]">
              <div className="flex justify-between"><span className="text-[#717680]">Subtotal</span><span className="font-mono-shelv">{formatINR(totals.subtotal)}</span></div>
              {form.is_igst ? (
                <div className="flex justify-between"><span className="text-[#717680]">IGST</span><span className="font-mono-shelv">{formatINR(totals.igst)}</span></div>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-[#717680]">CGST</span><span className="font-mono-shelv">{formatINR(totals.cgst)}</span></div>
                  <div className="flex justify-between"><span className="text-[#717680]">SGST</span><span className="font-mono-shelv">{formatINR(totals.sgst)}</span></div>
                </>
              )}
              <div className="flex justify-between border-t border-black/10 pt-1 font-bold text-base"><span>Grand total</span><span className="font-mono-shelv text-[#D90429]" data-testid="pur-grand-total">{formatINR(totals.grand_total)}</span></div>
            </div>
          </div>

          <div className="mt-3"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none"/></div>

          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="pur-save-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50">{saving ? "Saving…" : editingId ? "Update" : "Save Purchase"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PaymentModal
        open={!!payTarget}
        onOpenChange={(o) => !o && setPayTarget(null)}
        parentType="purchase"
        parentId={payTarget?.id}
        parentLabel={payTarget ? `${payTarget.vendor_name} · ${payTarget.bill_no || "Bill"} · ${formatINR(payTarget.grand_total)}` : ""}
        onUpdated={() => load()}
      />

      <PurchaseAutoFetchModal
        open={fetchOpen}
        onOpenChange={setFetchOpen}
        companyId={activeId}
        target="purchase"
        onParsed={handleAutoFetched}
      />
    </Layout>
  );
}

function Kpi({ label, value, sub, tone = "neutral", testid }) {
  const colors = {
    neutral: "text-[#0A0A0A]", ok: "text-[#0D9488]",
    warn: "text-[#F4A261]", danger: "text-[#D90429]",
  };
  return (
    <div className="p-5 border-r border-black/5 last:border-r-0" data-testid={testid}>
      <div className="font-mono-shelv text-[10px] tracking-widest uppercase text-[#D90429]">{label}</div>
      <div className={`font-display text-3xl font-black tracking-tighter mt-2 ${colors[tone] || colors.neutral}`}>{value}</div>
      {sub && <div className="text-[11px] text-[#717680] font-mono-shelv mt-1">{sub}</div>}
    </div>
  );
}
